import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { Tooltip, withStyles } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import { Storage } from 'aws-amplify';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import AttachFileIcon from '@/component/common/icons/AttachFileIcon';
import DownloadFileIcon from '@/component/common/icons/DownloadFileIcon';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import trI18n from '@/common/utils/trI18n';
import tr from '@/common/utils/tr';
import useTranslationSubscription from '@/hooks/useTranslation';

function ViewDocument({ documentData }) {
    useTranslationSubscription();
    
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);

    const fileName = useMemo(() => {
        if (documentData?.fileName) return documentData.fileName;
        // Extract filename from s3Path
        const s3Path = documentData?.s3Path || '';
        let extractedName = s3Path.split('/').pop() || 'Unknown file';
        // Pattern: {originalName}_{uuid}.{ext} - try to extract originalName
        const uuidPattern = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[^.]+)$/i;
        if (uuidPattern.test(extractedName)) {
            extractedName = extractedName.replace(uuidPattern, '$1');
        }
        return extractedName;
    }, [documentData]);

    const fileExtension = useMemo(() => {
        return Utils.getFileExtension(fileName) || '';
    }, [fileName]);

    const FlagMessageCopyLink = withStyles({
        tooltip: {
            color: '#1DA578',
            backgroundColor: '#D7F4E2',
            padding: '8px 16px',
            fontSize: '14px',
            boxShadow: '6px 5px 5px -4px rgba(0,0,0,0.3)'
        },
        arrow: {
            color: '#D7F4E2'
        }
    })(Tooltip);

    const downloadDocument = useCallback(async () => {
        try {
            let downloadUrl = documentData?.localUrl;

            // If no localUrl, get from S3
            if (!downloadUrl) {
                let s3Path = documentData?.s3Path || '';
                if (s3Path.startsWith('public/')) {
                    s3Path = s3Path.slice(7);
                }
                downloadUrl = await Storage.get(s3Path, { 
                    level: 'public',
                    expires: Constant.AI_PRESIGNED_URL_EXPIRE_TIME
                });
            }

            const response = await fetch(downloadUrl, { mode: 'cors' });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('downloadDocument error:', error);
        }
    }, [documentData, fileName]);

    const copyLinkDocument = useCallback(async () => {
        try {
            let localUrl = documentData?.localUrl;

            // If no localUrl or is blob, get from S3
            if (!localUrl || localUrl.startsWith('blob')) {
                let s3Path = documentData?.s3Path || '';
                if (s3Path.startsWith('public/')) {
                    s3Path = s3Path.slice(7);
                }
                localUrl = await Storage.get(s3Path, { 
                    level: 'public',
                    expires: Constant.AI_PRESIGNED_URL_EXPIRE_TIME
                });
            }

            await navigator.clipboard.writeText(localUrl);

            setIsShowFlagMessage(true);

            setTimeout(() => {
                setIsShowFlagMessage(false);
            }, 1000);

        } catch (err) {
            console.error('copyLinkDocument error:', err);
        }
    }, [documentData]);

    return (
        <div className="ai-preview-upload-item ai-preview-upload-file-item ai-view-document-item">
            <div className="ai-preview-file-icon">
                <AttachFileIcon extension={fileExtension} background="#FFFFFF" />
            </div>
            <div className="ai-preview-file-info">
                <OverflowTooltipContent
                    text={fileName}
                    className="ai-preview-file-name"
                    style={{ maxWidth: '200px' }}
                    placement='bottom'
                />
            </div>
            <div className="ai-view-document-options">
                <Tooltip title={trI18n('DOWNLOAD')} placement="top" arrow>
                    <button
                        className="btn-reset-default document-option-item"
                        onClick={downloadDocument}
                    >
                        <DownloadFileIcon />
                    </button>
                </Tooltip>
                <Tooltip title={trI18n('COPY_URL')} placement="top" arrow>
                    <button
                        className="btn-reset-default document-option-item"
                        onClick={copyLinkDocument}
                    >
                        <FlagMessageCopyLink
                            title={trI18n('KNOWDEMSG002')}
                            placement="top-start"
                            arrow={true}
                            open={isShowFlagMessage}
                        >
                            <LinkIcon className="ai-doc-copy-link-icon" />
                        </FlagMessageCopyLink>
                    </button>
                </Tooltip>
            </div>
        </div>
    );
}

ViewDocument.propTypes = {
    documentData: PropTypes.object.isRequired
};

export default ViewDocument;
